import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import Popover from 'ps-components/Popover/Popover';
import Input from 'ps-components/Input/Input';
import {
  formatDateUTC,
  getDataCutLabel,
  getDefaultOrField
} from 'ps-components/AdvancedTable/GenericGrid/GenericGridCellRenderers';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import './JobPricingCellRender.scss';
import classnames from 'classnames';
import CellIndicator from 'ps-components/LoadingIndicator/CellIndicator/CellIndicator';
import { Tooltip } from 'ps-design';
import { Tooltip as MantineTooltip } from '@mantine/core';

const COMMENT_CHAR_LIMIT = 80;

const editOverallComment = options => {
  return () => {
    options.setFocusedInput('comment-textarea-match');
    options.clearModifiedStates();
    options.jobPricingGridActions?.startEditingOverallComment(
      options.commentMatchId || options.adjustmentMatchId,
      options.saveMatch,
      options.editingOverallComment || options.editingOverallAdjustment,
      options.saveOverall
    );
  };
};

const editComment = (rowData, options) => {
  return () => {
    options.setFocusedInput('comment-textarea-match');
    options.clearModifiedStates();
    options.jobPricingGridActions?.startEditingComment(
      getDefaultOrField(rowData, 'match_id'),
      options.commentMatchId || options.adjustmentMatchId,
      options.saveMatch,
      options.editingOverallComment || options.editingOverallAdjustment,
      options.saveOverall
    );
  };
};

const EditComment = (initValue, options, isOverall) => {
  const isSlotted = options?.pricingType === 'slotted';

  return (
    <div
      className="survey-comment_textarea-container"
      style={{ width: isSlotted ? '100%' : options.meta.leftAlignSum - 200 }}
    >
      {options.readOnly ? (
        <div className="survey-comment--readonly">
          <i className="icon-doc-text" />
          <span>{initValue}</span>
        </div>
      ) : (
        <textarea
          ref={options.currentFocusRef}
          id="comment-textarea-match"
          className="survey-comment__textarea"
          onChange={isOverall ? options.onOverallCommentChange : options.onCommentChange}
          onKeyUp={isOverall ? options.handleKeyUpOnOverall : options.handleKeyUpOnJob}
          onBlur={isOverall ? options.handleOnBlurOverall : options.handleOnBlur}
          defaultValue={initValue}
        />
      )}
      {!options.readOnly && (
        <div className="survey-comment__instruction-text">
          <span className="bold">Enter</span> save
          <span className="bold space-left">Esc</span> cancel
        </div>
      )}
    </div>
  );
};

const OverallComposite = (rowData, options) => {
  const overallAddCommentRender = !options.editingOverallComment ? (
    <React.Fragment>
      {!options.readOnly && (
        <a onMouseDown={editOverallComment(options)}>
          <i className="icon-plus" />
          Add Overall Comment
        </a>
      )}
    </React.Fragment>
  ) : (
    EditComment(options.overallBenchmarkComment, options, true)
  );

  const overallCommentRender = !options.editingOverallComment ? (
    <div className="overall-comment-minimized" style={{ width: options.meta.leftAlignSum - 200 }}>
      <i className="icon-doc-text" />
      {options.readOnly ? (
        <div className="read-only-comment">{options.overallBenchmarkComment}</div>
      ) : (
        <a onMouseDown={editOverallComment(options)} title={options.overallBenchmarkComment}>
          {options.overallBenchmarkComment?.substring(0, COMMENT_CHAR_LIMIT) +
            (options.overallBenchmarkComment?.length > COMMENT_CHAR_LIMIT ? '...' : '')}
        </a>
      )}
    </div>
  ) : (
    EditComment(options.overallBenchmarkComment, options, true)
  );

  const overallAdjRender = !options.editingOverallAdjustment ? (
    <React.Fragment>
      Overall Adjustment
      <span className="overall-adjustment-text">
        {options.readOnly ? (
          <span>{options.overallBenchmarkAdjustment}</span>
        ) : (
          <a
            onMouseDown={() => {
              options.clearModifiedStates();
              options.setFocusedInput('overall-adjustment-match');
              options.jobPricingGridActions?.startEditingOverallAdjustment(
                options.commentMatchId || options.adjustmentMatchId,
                options.saveMatch,
                options.editingOverallComment || options.editingOverallAdjustment,
                options.saveOverall
              );
            }}
          >
            {options.overallBenchmarkAdjustment}
          </a>
        )}
      </span>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Input
        type="float"
        id="overall-adjustment-match"
        step="0.001"
        defaultValue={options.overallBenchmarkAdjustment}
        onChange={options.onOverallAdjChange}
        className="pricing-details-adjustment__input"
        onKeyUp={options.handleKeyUpOnOverall}
        onBlur={options.handleOnBlurOverall}
        passedRef={options.currentFocusRef}
      />
      <span className="pricing-details__save-instruction">
        <div>
          <span className="bold">Enter</span> <span className="text"> save</span>
        </div>
        <div>
          <span className="bold">Esc</span> <span className="text"> cancel</span>
        </div>
      </span>
    </React.Fragment>
  );

  function getBenchmarkSeparator(options) {
    if (options?.overallBenchmarkComment && !options.hideOverallBenchmarkComment) {
      return '|';
    }
    if (options?.readOnly) {
      return '';
    }
  }

  return (
    <div className="default-cell__composites" style={{ width: options.meta.leftAlignSum - 50 }}>
      <div className="overall-composite__text">Overall Composite</div>
      <div className="overall-adjustment">
        {!options.hideOverallBenchmarkAdj && overallAdjRender} {getBenchmarkSeparator()}{' '}
        {options.overallBenchmarkComment && !options.hideOverallBenchmarkComment
          ? overallCommentRender
          : overallAddCommentRender}
      </div>
    </div>
  );
};

export const PricingDetails = (rowData, key, options) => {
  const pricingType = options.pricingType;

  return pricingType === 'slotted'
    ? SlottedDetails(rowData, key, options)
    : SurveyDetails(rowData, key, { ...options, excludeLoadingText: true });
};

export const PricingDetailsSubRow = (rowData, key, options) => {
  const { getSurveyJobDetailsPageHref, meta } = options;

  const cutId = getDefaultOrField(rowData, 'cut_id') || rowData.cutId;
  const surveyJobDetailsHref = getSurveyJobDetailsPageHref(cutId);
  const isParticipationMatch = getDefaultOrField(rowData, 'participation_p');
  const matchJobDescription = getDefaultOrField(rowData, 'job_description') || 'No description';

  return (
    <Fragment>
      <div className="default-cell__survey slotted-sub-row">
        <div className="default-cell__title default-cell__sub-row-title">
        <MantineTooltip maw='80%' multiline transitionProps={{ transition: 'fade-up', duration: 500 }} label={matchJobDescription} openDelay={350} withArrow>
          <a className="default-cell__title" href={surveyJobDetailsHref} target="_blank" rel="noreferrer">
            {getDefaultOrField(rowData, 'job_title') || 'No title'}
          </a>
          </MantineTooltip>
          <span className="default-cell__code">{getDefaultOrField(rowData, 'job_code') || ''}</span>
        </div>

        <div className="default-cell__sub-text">
          <span className="default-cell__data-cut">{getDefaultOrField(rowData, 'job_scope')}</span>
          <span className="default-cell__survey-short-name">
            {getDefaultOrField(rowData, 'pub_survey_short_name') || ''}
          </span>
        </div>

        <div className="description-row">
            <React.Fragment>
              <CheckboxInput
                className="participation-checkbox"
                disabled={true}
                checked={isParticipationMatch}
                onClick={null}
                tooltipText="Open job details to update the participation match indicator for this survey job"
              />
              <span className="participation-text">Participation match</span>
            </React.Fragment>

        </div>
      </div>

      {meta?.isLastSubRow && meta?.getComment()}
    </Fragment>
  );
};

export const SlottedDetails = (rowData, key, options) => {
  if (options.isSummary) {
    return OverallComposite(rowData, options);
  }

  const jobPricingGridActions = options.jobPricingGridActions;
  const slottedDetailsLoading = options.slottedDetailsLoading; // array of org job ids
  const slottedExpandedSections = options.slottedExpandedSections; // object of org job ids
  const surveysForSlottedMatches = options.surveysForSlottedMatches; // object of org job ids

  const orgJobId = getDefaultOrField(rowData, 'org_job_id');
  const orgJobCode = getDefaultOrField(rowData, 'org_job_code');
  const orgJobCodeKey = getDefaultOrField(rowData, 'org_job_code_key');
  const orgJobTitle = getDefaultOrField(rowData, 'org_job_title');
  const orgJobDescription = getDefaultOrField(rowData, 'org_job_description') || 'No description';

  const isExpandedLoading = slottedDetailsLoading?.includes(orgJobId);
  const showExpandedSection = slottedExpandedSections[orgJobId] === true;
  const detailsAlreadyLoaded = surveysForSlottedMatches[orgJobId] !== undefined;

  return (
    <div className="default-cell__survey slotted">
      {!!options.getSlottedJobPageHref ? (
        <MantineTooltip maw='80%' multiline transitionProps={{ transition: 'fade-up', duration: 500 }} label={orgJobDescription} openDelay={350} withArrow disabled={!orgJobDescription}>
          <a
            className="data__cell-title default-cell__title default-cell__pricing-title"
            href={options.getSlottedJobPageHref(orgJobId)}
            target="_blank"
            rel="noreferrer"
          >
            {orgJobTitle}
          </a>
        </MantineTooltip>
      ) : (
        <MantineTooltip maw='80%' multiline transitionProps={{ transition: 'fade-up', duration: 500 }} label={orgJobDescription} openDelay={350} withArrow disabled={!orgJobDescription}>
          <span className="data__cell-title default-cell__title default-cell__pricing-title">{orgJobTitle}</span>
        </MantineTooltip>
      )}
      <div className="default-cell__sub-text">
        {orgJobCode ? orgJobCode : ''}
        {options.useJobCodeKey && orgJobCodeKey?.trim() ? ` - ${orgJobCodeKey}` : ''}
      </div>

      {!!jobPricingGridActions.toggleSlottedExpandedSection ? (
        <div className="default-cell__sub-text">
          <div
            className="default-cell__view-details-text slotted-sub-expand-text"
            onClick={() => {
              if (!showExpandedSection) {
                if (detailsAlreadyLoaded) {
                  jobPricingGridActions.toggleSlottedExpandedSection(orgJobId, true);
                } else {
                  options.getPricingDetailsForSlottedJob({
                    title: orgJobTitle,
                    id: orgJobId,
                    code: orgJobCode,
                    codeKey: orgJobCodeKey
                  });
                }
              } else {
                jobPricingGridActions.toggleSlottedExpandedSection(orgJobId, false);
              }
            }}
          >
            <div className="sub-row-matches-text">
              {getDefaultOrField(rowData, 'data_adjustment_factor_reason') ? 'Matches with comments' : 'Matches'}
            </div>
            {isExpandedLoading ? (
              <CellIndicator iconClass="icon-animate-spin icon-spin cell-indicator" />
            ) : (
              <i className={classnames('icon-down-open', { flipped: showExpandedSection })}></i>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const SlottedDetailsComment = (row, options) => {
  const rowData = row?.values;
  const matchId = getDefaultOrField(rowData, 'match_id');
  const isParticipationMatch = getDefaultOrField(rowData, 'participation_p');
  const reason = getDefaultOrField(rowData, 'data_adjustment_factor_reason');
  const { readOnly, selectedBenchmarkYear, editPastYears } = options;
  const isParticipationChecking = options.checkingParticipationIDs.includes(matchId);
  const isParticipationUnchecking = options.uncheckingParticipationIDs.includes(matchId);
  const isParticipationLoading = isParticipationChecking || isParticipationUnchecking;
  options.isSlotted = true;

  const details = [];
  const tooltipText = 'Will use survey job matches from this slotted job that are marked for participation';

  const thisId = getDefaultOrField(rowData, 'org_job_id');
  let subParticipationCount = 0;
  const partIndex = options?.surveysForSlottedMatches[thisId]?.terms.findIndex(
    term => term.term === 'participation_p_default'
  );

  options?.surveysForSlottedMatches[thisId]?.data.forEach(dataRow => {
    if (dataRow[partIndex]) subParticipationCount++;
  });

  if ('field_data_adjustment_factor_reason' in rowData || 'data_adjustment_factor_reason_default' in rowData) {
    details.push(
      <div className="default-cell__comment">
        {!reason && AddCommentRender(matchId, rowData, options)}
        {reason && CommentRender(matchId, rowData, options)}
      </div>
    );
  }

  if (options.siteSettings.useParticipationMatch) {
    details.push(
      <div className="participation-container">
        {isParticipationLoading ? (
          <CellIndicator iconClass="icon-animate-spin icon-spin cell-indicator participation-loading" />
        ) : (
          <CheckboxInput
            className="participation-checkbox"
            disabled={readOnly}
            checked={isParticipationMatch}
            onClick={() => {
              if (!readOnly)
                (isParticipationMatch
                  ? options.jobPricingGridActions.checkParticipation(
                      options.orgJobId,
                      matchId,
                      false,
                      selectedBenchmarkYear,
                      editPastYears
                    )
                  : options.jobPricingGridActions.checkParticipation(
                      options.orgJobId,
                      matchId,
                      true,
                      selectedBenchmarkYear,
                      editPastYears
                    )
                ).then(() => {
                  options.jobPricingGridActions.updateSpecificRowData(
                    options.index,
                    [options.participationIndex],
                    [!isParticipationMatch]
                  );
                });
            }}
          />
        )}

        <div className="participation-text">{`Participation matches (${subParticipationCount})`}</div>

        <Tooltip
          className="checkbox-tooltip"
          hoverText={tooltipText}
          id={`checkbox-tooltip-${matchId}`}
          position="right"
        />
      </div>
    );
  }

  return <div className="market-pricing__slotted-details-bottom">{details}</div>;
};

const AddCommentRender = (matchId, rowData, options) => {
  return options.commentMatchId !== matchId ? (
    <React.Fragment>
      {!options.readOnly && (
        <a onMouseDown={editComment(rowData, options)}>
          <i className="icon-plus" />
          Add Comment
        </a>
      )}
    </React.Fragment>
  ) : (
    EditComment(getDefaultOrField(rowData, 'data_adjustment_factor_reason'), options)
  );
};

export const CommentRender = (matchId, rowData, options) => {
  const reason = getDefaultOrField(rowData, 'data_adjustment_factor_reason_default');
  return options.commentMatchId !== matchId ? (
    <div className="overall-comment-minimized" style={{ width: options.meta.leftAlignSum - 200 }}>
      <i className="icon-doc-text" />
      {options.readOnly ? (
        <div className="read-only-comment">{reason}</div>
      ) : (
        <a onMouseDown={editComment(rowData, options)} title={reason}>
          {reason?.substring(0, COMMENT_CHAR_LIMIT) + (reason?.length > COMMENT_CHAR_LIMIT ? '...' : '')}
        </a>
      )}
    </div>
  ) : (
    EditComment(reason, options)
  );
};

export const SurveyDetails = (rowData, key, options) => {
  const {
    isRowLoading,
    isRowDeleting,
    loadingText,
    deletingText,
    addedMatches,
    excludeLoadingText,
    getSurveyJobDetailsPageHref,
    selectedBenchmarkYear,
    editPastYears
  } = options;

  const cutId = getDefaultOrField(rowData, 'cut_id') || rowData.cutId;
  const matchId = getDefaultOrField(rowData, 'match_id');
  const effDate =
    getDefaultOrField(rowData, 'pub_survey_effective_date') || getDefaultOrField(rowData, 'effective_date');
    const jobTitle = getDefaultOrField(rowData, 'job_title') || 'No Title';
  const adjReason = getDefaultOrField(rowData, 'data_adjustment_factor_reason');
  const totalCompanies = getDefaultOrField(rowData, 'total_companies');
  const totalEmployees = getDefaultOrField(rowData, 'total_incumbents');
  const currency = getDefaultOrField(rowData, 'currency');
  const description = getDefaultOrField(rowData, 'job_description');

  const { renderPopover, togglePopover, selectedRowId } = options;
  const jobDescriptionLinkRef = React.createRef();
  const addCommentRender = AddCommentRender(matchId, rowData, options);
  const commentRender = CommentRender(matchId, rowData, options);
  const surveyJobDetailsHref = getSurveyJobDetailsPageHref && getSurveyJobDetailsPageHref(cutId);
  const readOnly = options.readOnly;
  const isParticipationMatch = getDefaultOrField(rowData, 'participation_p');
  const isParticipationChecking = options.checkingParticipationIDs.includes(matchId);
  const isParticipationUnchecking = options.uncheckingParticipationIDs.includes(matchId);
  const isParticipationLoading = isParticipationChecking || isParticipationUnchecking;

  let participationText = 'Participation Match';
  if (isParticipationChecking) {
    participationText = 'Marking match...';
  } else if (isParticipationUnchecking) {
    participationText = 'Unmarking match...';
  }

  if (options.isSummary) {
    return OverallComposite(rowData, options);
  }
  if (options.isMarketComparison) {
    return <div className="default-cell__market-comparison" style={{ width: options.meta.leftAlignSum - 50 }} />;
  }
  const showDetails = options.matchesCollapsed.includes(matchId);
  return (
    <div className="default-cell__survey">
      {!excludeLoadingText && (
        <div className="loading-text">
          {isRowLoading
            ? isRowDeleting
              ? deletingText
              : loadingText
            : addedMatches?.includes(cutId) && <span className="data__cell-added-match">Added to Pricing</span>}
        </div>
      )}

      <div className="default-cell__title default-cell__pricing-title">
        {surveyJobDetailsHref ? (
          <MantineTooltip maw='80%' multiline transitionProps={{ transition: 'fade-up', duration: 500 }} label={description} openDelay={350} withArrow disabled={!description}>
            <a className="default-cell__title" href={surveyJobDetailsHref} target="_blank" rel="noreferrer">
              {jobTitle}
            </a>
          </MantineTooltip>
        ) : (
          <MantineTooltip maw='80%' multiline transitionProps={{ transition: 'fade-up', duration: 500 }} label={description} openDelay={350} withArrow disabled={!description}>
            <span className="default-cell__title">{jobTitle}</span>
          </MantineTooltip>
        )}
        <span className="default-cell__code">{getDefaultOrField(rowData, 'job_code') || ''}</span>
      </div>

      <div className="default-cell__sub-text">
        <span className="default-cell__data-cut">{getDataCutLabel(rowData, options, '', 'right')}</span>
        <span className="default-cell__survey-short-name">
          {getDefaultOrField(rowData, 'pub_survey_short_name') || ''}
        </span>
        {!!options.customMatchDetailsRenderer ? (
          options.customMatchDetailsRenderer(rowData, key, options)
        ) : (
          <React.Fragment>
            <div
              className="default-cell__view-details-text"
              onMouseDown={options.handleMatchToggle.bind(this, matchId)}
            >
              Details {adjReason ? 'with comments' : ''}
              <i className={classnames('icon-down-open', { flipped: showDetails })}></i>
            </div>
            <div
              style={{ width: options.meta.leftAlignSum - 50 }}
              className={classnames('default-cell__view-details', showDetails ? 'show' : 'hide')}
            >
              <span className="extra-details-container">
                {`${totalCompanies || '-'} cos,
                ${totalEmployees || '-'} ees,
                ${currency ? `${currency},` : ''} eff ${formatDateUTC(effDate)}`
                }
              </span>
              {options.siteSettings.useParticipationMatch && (
                <span
                  className={classnames(
                    {
                      'participation-loading-container': isParticipationLoading
                    },
                    'participation-container'
                  )}
                >
                  {<span className="divider">|</span>}
                  <div className="participation-icon-container">
                    {isParticipationLoading ? (
                      <i className="participation-loading icon-animate-spin icon-spin" />
                    ) : (
                      <CheckboxInput
                        className={'participation-checkbox'}
                        id="participation-checkbox"
                        disabled={readOnly}
                        checked={isParticipationMatch}
                        onClick={() => {
                          if (!readOnly)
                            (isParticipationMatch
                              ? options.jobPricingGridActions.checkParticipation(
                                  options.orgJobId,
                                  matchId,
                                  false,
                                  selectedBenchmarkYear,
                                  editPastYears
                                )
                              : options.jobPricingGridActions.checkParticipation(
                                  options.orgJobId,
                                  matchId,
                                  true,
                                  selectedBenchmarkYear,
                                  editPastYears
                                )
                            ).then(() => {
                              options.jobPricingGridActions.updateSpecificRowData(
                                options.index,
                                [options.participationIndex],
                                [!isParticipationMatch]
                              );
                            });
                        }}
                      />
                    )}
                  </div>
                  {participationText}
                </span>
              )}
              {cutId === selectedRowId &&
                ReactDOM.createPortal(
                  <Popover
                    triggerElement={jobDescriptionLinkRef}
                    relativeElement={document.querySelector('div#advanced-table__popover')}
                    popoverText={description}
                    display={renderPopover}
                    togglePopover={togglePopover}
                  />,
                  document.getElementById('advanced-table__popover')
                )}
              {(rowData.hasOwnProperty('field_data_adjustment_factor_reason') ||
                rowData.hasOwnProperty('data_adjustment_factor_reason_default')) && (
                <div className="default-cell__comment">
                  {!adjReason && addCommentRender}
                  {adjReason && commentRender}
                </div>
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
